import {useQueryClient} from '@tanstack/react-query'
import {useCallback, useEffect, useMemo, useState} from 'react'

import {usePaginatedViewVariablesContext} from '../memex-items/context/paginated-view-variables'
import {pageParamForNextPlaceholder} from '../memex-items/queries/types'
import {usePaginatedMemexItemsQuery} from '../memex-items/queries/use-paginated-memex-items-query'
import {EagerQueryInvalidationSingleton} from './eager-query-invalidation'
import {mostRecentUpdateSingleton} from './most-recent-update'
import {pendingUpdatesSingleton} from './pending-updates'

export const useHandlePaginatedDataRefresh = () => {
  const {invalidateAllQueries, queryKeysForGroups, queryKeysForItems, queryKeysForSecondaryGroups, isFetching} =
    usePaginatedMemexItemsQuery()
  const {clearStaleVariable, staleVariable} = usePaginatedViewVariablesContext() ?? {}
  const queryClient = useQueryClient()
  const [isRefreshingByKey, setIsRefreshingByKey] = useState<Record<string, boolean>>({})
  const variablesKey = JSON.stringify(queryKeysForItems[0]?.[2]) ?? ''
  const hasPlaceholderData = useMemo(
    () => queryKeysForItems.some(key => key[4] === pageParamForNextPlaceholder),
    [queryKeysForItems],
  )
  if (!hasPlaceholderData && isRefreshingByKey[variablesKey]) {
    setIsRefreshingByKey(prev => {
      const newState = {...prev}
      delete newState[variablesKey]
      return newState
    })
  }

  useEffect(() => {
    if (!isFetching && staleVariable) {
      clearStaleVariable?.()
    }
  }, [isFetching, clearStaleVariable, staleVariable])

  const handleCancelFetchData = useCallback(() => {
    for (const queryKey of queryKeysForGroups) {
      // no need to cancel placeholder queries
      if (queryKey[4] !== pageParamForNextPlaceholder) {
        queryClient.cancelQueries({queryKey})
      }
    }
    for (const queryKey of queryKeysForSecondaryGroups) {
      // no need to cancel placeholder queries
      if (queryKey[4] !== pageParamForNextPlaceholder) {
        queryClient.cancelQueries({queryKey})
      }
    }
    for (const queryKey of queryKeysForItems) {
      // no need to cancel placeholder queries
      if (queryKey[4] !== pageParamForNextPlaceholder) {
        queryClient.cancelQueries({queryKey})
      }
    }
  }, [queryKeysForGroups, queryClient, queryKeysForSecondaryGroups, queryKeysForItems])

  const handleRefresh = useCallback(
    async (timestamp?: number, requestId?: string) => {
      // if the timestamp is older than the most recent update, then refreshing the paginated data may overwrite
      // the local state with stale denormalized state, so we ignore it
      const isMostRecentUpdate =
        // the provided timestamp (from hydro) is in the precision of seconds, so we have to reduce the locally stored
        // time to that precision (flooring so denormalizations on the same second get through)
        (timestamp || Infinity) >= Math.floor(mostRecentUpdateSingleton.get() / 1000) &&
        !pendingUpdatesSingleton.hasPendingUpdates()

      // If we eagerly invalidated the query cache for this request then we don't need to do it again
      const wasPreviouslyInvalidated = requestId && EagerQueryInvalidationSingleton.has(requestId)

      if (isMostRecentUpdate && !wasPreviouslyInvalidated) {
        handleCancelFetchData()
        setIsRefreshingByKey(prev => ({...prev, [variablesKey]: true}))
        return invalidateAllQueries()
      }
    },
    [invalidateAllQueries, handleCancelFetchData, variablesKey],
  )

  return {handleRefresh, handleCancelFetchData, isRefreshing: !!isRefreshingByKey[variablesKey]}
}
